import type { VisitStatus } from '@empower-safety/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from './client.js';

export interface VisitDto {
  id: string;
  organisation_id: string;
  user_id: string;
  user_display_name: string | null;
  external_identity_id: string | null;
  provider: string;
  external_visit_id: string | null;
  client_display_name: string | null;
  service_type: string | null;
  location_address: string | null;
  lat: number | null;
  lng: number | null;
  scheduled_start: string;
  scheduled_end: string;
  actual_arrival: string | null;
  actual_departure: string | null;
  status: VisitStatus;
  notes: string | null;
  // Driving leg from the worker's previous active visit of the day to this one,
  // measured via the OSRM routing service. Null on the first visit of a day, on
  // cancelled/no_show rows, when either endpoint lacks lat/lng, or when the routing
  // service is unavailable.
  travel_km_from_previous: number | null;
  travel_duration_s_from_previous: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateVisitInput {
  user_id: string;
  scheduled_start: string;
  scheduled_end: string;
  client_display_name?: string | null;
  service_type?: string | null;
  location_address?: string | null;
  lat?: number | null;
  lng?: number | null;
  notes?: string | null;
  status?: VisitStatus;
}

export interface UpdateVisitInput extends Partial<CreateVisitInput> {
  id: string;
  actual_arrival?: string | null;
  actual_departure?: string | null;
}

export interface VisitsFilter {
  user_id?: string;
  status?: VisitStatus;
  from?: string;
  to?: string;
  provider?: string;
}

export function useVisits(filter: VisitsFilter = {}) {
  const api = useApi();
  return useQuery({
    queryKey: ['visits', filter],
    queryFn: () => {
      const qs = new URLSearchParams();
      if (filter.user_id) qs.set('user_id', filter.user_id);
      if (filter.status) qs.set('status', filter.status);
      if (filter.from) qs.set('from', filter.from);
      if (filter.to) qs.set('to', filter.to);
      if (filter.provider) qs.set('provider', filter.provider);
      qs.set('limit', '500');
      return api<{ visits: VisitDto[] }>(`/visits?${qs.toString()}`);
    },
    staleTime: 15_000,
  });
}

export function useCreateVisit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateVisitInput) =>
      api<{ visit: VisitDto }>('/visits', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visits'] }),
  });
}

export function useUpdateVisit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: UpdateVisitInput) =>
      api<{ visit: VisitDto }>(`/visits/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(body),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visits'] }),
  });
}

export function useDeleteVisit() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api<void>(`/visits/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['visits'] }),
  });
}
